import { fetchWithTimeout } from "@fetch/fetch";
import { fetchEsummary } from "@fetch/genbank/esummary";
import { type GenbankRecord, parseGenbank } from "@formats/genbank/parse";
import { formatBp } from "@lib/genetics";

const EUTILS_BASE = "https://eutils.ncbi.nlm.nih.gov/entrez/eutils/";

export const ACCESSION_SIZE_LIMIT = 10_000_000;

export type NcbiCredentials = {
  apiKey: string;
  email: string;
};

export async function ncbiFetch(
  url: string,
  params: Record<string, string>,
  credentials?: NcbiCredentials,
  signal?: AbortSignal,
): Promise<Response> {
  const searchParams = new URLSearchParams(params);

  if (credentials?.apiKey) {
    searchParams.set("api_key", credentials.apiKey);
  }

  if (credentials?.email) {
    searchParams.set("email", credentials.email);
  }

  return fetchWithTimeout(`${EUTILS_BASE}${url}?${searchParams.toString()}`, {
    signal,
    timeoutMs: 30_000,
  });
}

export async function fetchGenbank(
  accession: string,
  credentials?: NcbiCredentials,
  signal?: AbortSignal,
): Promise<string> {
  const response = await ncbiFetch(
    "efetch.fcgi",
    {
      db: "nucleotide",
      id: accession,
      rettype: "gb",
      retmode: "text",
    },
    credentials,
    signal,
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch GenBank record for '${accession}': ${response.status} ${response.statusText}`,
    );
  }

  return response.text();
}

export type BatchFetchResult = {
  records: Map<string, GenbankRecord>;
  errors: Map<string, string>;
};

export async function fetchGenbankBatch(
  accessions: string[],
  credentials?: NcbiCredentials,
  signal?: AbortSignal,
): Promise<BatchFetchResult> {
  const records = new Map<string, GenbankRecord>();
  const errors = new Map<string, string>();

  if (accessions.length === 0) {
    return { records, errors };
  }

  let accessionsToFetch = accessions;

  try {
    const summaries = await fetchEsummary(accessions, credentials, signal);

    if (summaries.size > 0) {
      accessionsToFetch = accessions.filter((accession) => {
        const summary = summaries.get(accession);

        if (summary && summary.length > ACCESSION_SIZE_LIMIT) {
          errors.set(
            accession,
            `Sequence too large (${formatBp(summary.length)}). Import as a FASTA file instead.`,
          );
          return false;
        }

        return true;
      });
    }
  } catch {
    // esummary failed — fall through to GenBank fetch for all accessions.
  }

  if (accessionsToFetch.length === 0) {
    return { records, errors };
  }

  const response = await ncbiFetch(
    "efetch.fcgi",
    {
      db: "nucleotide",
      id: accessionsToFetch.join(","),
      rettype: "gb",
      retmode: "text",
    },
    credentials,
    signal,
  );

  if (!response.ok) {
    for (const accession of accessionsToFetch) {
      errors.set(accession, `Fetch failed: ${response.status}`);
    }
    return { records, errors };
  }

  const text = await response.text();
  const result = parseGenbank(text);

  for (const record of result.result) {
    const accession = record.accession.split(/\s+/)[0];
    records.set(accession, record);

    if (record.version) {
      const version = record.version.split(/\s+/)[0];
      records.set(version, record);
    }
  }

  for (const accession of accessionsToFetch) {
    if (!records.has(accession)) {
      errors.set(accession, "Not found");
    }
  }

  return { records, errors };
}
